/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  FormInstance,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
import { useIntl } from 'react-intl';

import { useState } from 'react';

import {
  AppPageHeader,
  CompaniesInfiniteSelectField,
  DatePicker,
} from '@/components/_common';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import getInventoryReasons from '@/core/domain/inventory/inventories/getInventoriesReasons';
import {
  CheckedType,
  Inventory,
  InventoryFormValues,
  InventoryStatus,
  InventoryStatusColor,
  ListItemInventoryParams,
} from '@/core/domain/inventory/inventories/Inventory.types';
import InventoryItemsExtraFilters from '../InventoryItemsExtraFilters';
import InventoryItemsList from '../InventoryItemsList';
import StatusTag from '@/components/_common/StatusTag';
import getInventoryStatus from '@/core/domain/inventory/inventories/getInventoriesStatus';
import { useDebounce } from '@/hooks';
import InventoryRepeatButtons from '../InventoryRepeatButtons';
import { useItemsInventory } from '@/core/domain/inventory/inventories/inventory.hooks';

interface Props {
  form: FormInstance<InventoryFormValues>;
  inventory: Inventory;
}

function InventoryDetails({ form, inventory }: Props) {
  const intl = useIntl();
  const { Title } = Typography;

  const [queryParams, setQueryParams] = useState<ListItemInventoryParams>({
    order: 'ASC',
    orderColumns: 'MANUFACTURING_CODE',
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const inventoryItems = useItemsInventory(Number(inventory.id), queryDebounce);

  function handleTitleInventoryDetail() {
    const reasonFinded = getInventoryStatus().find(
      (status) => status.value === inventory?.status,
    );
    if (
      inventory?.status === InventoryStatus.PROCESSING ||
      inventory?.status === InventoryStatus.IMPORTING ||
      inventory?.status === InventoryStatus.DONE
    ) {
      return (
        <StatusTag
          label={intl.formatMessage({ id: reasonFinded?.labelId })}
          color={InventoryStatusColor[inventory?.status]}
          style={{ fontSize: 20, padding: 10 }}
        />
      );
    } else if (inventory?.currentCount?.countNumber) {
      return (
        <StatusTag
          label={
            inventory?.currentCount.countNumber +
            intl.formatMessage({
              id: 'inventory.detailPage.fetchItems.message.firstCount',
            })
          }
          style={{ fontSize: 20, padding: 10 }}
          color="blue"
        />
      );
    }
  }

  const disable =
    inventory?.status === InventoryStatus.DONE ||
    inventory?.status === InventoryStatus.PROCESSING;

  return (
    <>
      <Form layout="vertical" form={form}>
        <Row gutter={[16, 9]}>
          <Col xs={24} sm={24} md={9}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'inventory.detailPage.form.company',
              })}
              name={['company', 'id']}
            >
              <CompaniesInfiniteSelectField disabled />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'inventory.detailPage.form.inventoryReason',
              })}
              name="reason"
            >
              <Select
                disabled={disable}
                allowClear
                style={{ width: '100%' }}
                options={getInventoryReasons().map((reason) => ({
                  value: reason.value,
                  label: intl.formatMessage({ id: reason.labelId }),
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={7}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'inventory.detailPage.form.date',
              })}
              name="date"
            >
              <DatePicker disabled={disable} />
            </FloatingFormItem>
          </Col>
        </Row>

        <Divider>
          <Title level={5} style={{ textAlign: 'center' }}>
            {handleTitleInventoryDetail()}
          </Title>
        </Divider>

        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              nextToken: undefined,
              name: value,
            }))
          }
          searchValue={queryParams.name}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() =>
                  setQueryParams({
                    order: 'ASC',
                    orderColumns: 'MANUFACTURING_CODE',
                  })
                }
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <InventoryItemsExtraFilters
              queryParams={queryParams}
              setQueryParams={(run) => {
                setQueryParams((prev) => ({
                  ...prev,
                  ...(typeof run === 'function' ? run(prev) : run),
                  nextToken: undefined,
                }));
              }}
            />
          }
        />

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              label="Exibir:"
              initialValue="all"
              style={{ marginBottom: 0 }}
            >
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                size="large"
                defaultValue={CheckedType.all}
                onChange={({ target: { value } }) => {
                  const checkedType =
                    value === CheckedType.all
                      ? undefined
                      : value === CheckedType.checked;

                  setQueryParams((prev) => ({
                    ...prev,
                    checked: checkedType,
                    nextToken: undefined,
                  }));
                }}
              >
                <Radio.Button value={CheckedType.all}>
                  Todos os itens
                </Radio.Button>
                <Radio.Button value={CheckedType.checked}>
                  Somente itens marcados
                </Radio.Button>
                <Radio.Button value={CheckedType.unchecked}>
                  Somente itens desmarcados
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Checkbox
              checked={queryParams?.sortByUpdatedAt}
              onChange={({ target: { checked } }) =>
                setQueryParams((prev) => ({
                  ...prev,
                  sortByUpdatedAt: checked,
                }))
              }
            >
              Exibir itens modificados por último
            </Checkbox>
          </Col>

          <Alert
            type="warning"
            showIcon
            message="Atenção: ao finalizar o inventário, apenas os itens que estiverem marcados terão suas quantidades movimentadas no estoque. Os itens não selecionados não sofrerão nenhuma alteração."
            style={{ marginBottom: 16 }}
          />

          {inventory?.processPrices && (
            <Col span={24}>
              <Alert
                type="success"
                showIcon
                message="Valores alterados na finalização do inventário"
              />
            </Col>
          )}

          <Col span={24}>
            <InventoryItemsList
              filter={queryDebounce}
              setFilter={setQueryParams}
              form={form}
              inventory={inventory}
              inventoryItemsQueryResult={inventoryItems}
            />
          </Col>
        </Row>

        <Divider />

        <InventoryRepeatButtons
          inventory={inventory}
          inventoryId={inventory.id}
          inventoryItemsInfiniteData={inventoryItems.data}
          formInventory={form}
        />
      </Form>
    </>
  );
}

export default InventoryDetails;
